import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Image as ImageIcon, Save, RefreshCw } from 'lucide-react';

const API = '/api';

export const BrandingManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState({
    logo_url: '/rsj-logo.svg',
    logo_dark_url: null,
    favicon_url: '/favicon.ico',
    platform_name: 'RhinoShield Platform',
    company_name: 'RhinoShield Florida',
    tagline: '',
    login_title: 'Welcome Back',
    login_subtitle: 'Enter your credentials to access your dashboard',
    login_background_url: 'https://customer-assets.emergentagent.com/job_rs-workspace/artifacts/ltngn5pv_H11.jpg'
  });

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/branding`);
      setBranding(response.data);
    } catch (error) {
      console.error('Error fetching branding:', error);
      toast.error('Failed to load branding configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setBranding({
      ...branding,
      [field]: value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/branding`, branding);
      toast.success('Branding updated successfully! Reload the page to see changes.');
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading branding manager...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ImageIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">Branding Manager</h1>
              <p className="text-muted-foreground">Customize platform logo and branding</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleReload}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload Page
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Logo Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Logo Preview</CardTitle>
            <CardDescription>Current logo displayed in the sidebar</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <div className="p-6 border rounded-lg bg-background">
                <p className="text-xs text-muted-foreground mb-2">Light Mode</p>
                <img 
                  src={branding.logo_url} 
                  alt="Logo Preview" 
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="40"><text x="10" y="25" fill="gray">Logo Error</text></svg>';
                  }}
                />
              </div>
              {branding.logo_dark_url && (
                <div className="p-6 border rounded-lg bg-gray-900">
                  <p className="text-xs text-gray-400 mb-2">Dark Mode</p>
                  <img 
                    src={branding.logo_dark_url} 
                    alt="Dark Logo Preview" 
                    className="h-12 w-auto"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Logo Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Logo Configuration</CardTitle>
            <CardDescription>Set logo URLs (relative paths or full URLs)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Logo URL *</Label>
              <Input
                value={branding.logo_url}
                onChange={(e) => handleChange('logo_url', e.target.value)}
                placeholder="/logo.svg or https://example.com/logo.svg"
              />
              <p className="text-xs text-muted-foreground">
                Path to your logo file (e.g., /rsj-logo.svg). Files should be placed in /app/frontend/public/
              </p>
            </div>

            <div className="space-y-2">
              <Label>Dark Mode Logo URL (Optional)</Label>
              <Input
                value={branding.logo_dark_url || ''}
                onChange={(e) => handleChange('logo_dark_url', e.target.value || null)}
                placeholder="Leave empty to use same logo"
              />
              <p className="text-xs text-muted-foreground">
                Optional separate logo for dark mode
              </p>
            </div>

            <div className="space-y-2">
              <Label>Favicon URL</Label>
              <Input
                value={branding.favicon_url}
                onChange={(e) => handleChange('favicon_url', e.target.value)}
                placeholder="/favicon.ico"
              />
              <p className="text-xs text-muted-foreground">
                Browser tab icon (typically .ico or .png)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Platform Names */}
        <Card>
          <CardHeader>
            <CardTitle>Platform Information</CardTitle>
            <CardDescription>Platform and company names</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Platform Name</Label>
              <Input
                value={branding.platform_name}
                onChange={(e) => handleChange('platform_name', e.target.value)}
                placeholder="RhinoShield Platform"
              />
              <p className="text-xs text-muted-foreground">
                Shown below logo on login page
              </p>
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={branding.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="RhinoShield Florida"
              />
            </div>

            <div className="space-y-2">
              <Label>Tagline (Optional)</Label>
              <Input
                value={branding.tagline || ''}
                onChange={(e) => handleChange('tagline', e.target.value)}
                placeholder="Your platform tagline"
              />
            </div>
          </CardContent>
        </Card>

        {/* Login Page Customization */}
        <Card>
          <CardHeader>
            <CardTitle>Login Page Customization</CardTitle>
            <CardDescription>Customize text on the login page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Login Title</Label>
              <Input
                value={branding.login_title || ''}
                onChange={(e) => handleChange('login_title', e.target.value)}
                placeholder="Welcome Back"
              />
              <p className="text-xs text-muted-foreground">
                Main heading on login card
              </p>
            </div>

            <div className="space-y-2">
              <Label>Login Subtitle</Label>
              <Input
                value={branding.login_subtitle || ''}
                onChange={(e) => handleChange('login_subtitle', e.target.value)}
                placeholder="Enter your credentials to access your dashboard"
              />
              <p className="text-xs text-muted-foreground">
                Subtitle below login title
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Login Background */}
        <Card>
          <CardHeader>
            <CardTitle>Login Page Background</CardTitle>
            <CardDescription>Customize the background image on the login page</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Background Image URL</Label>
              <Input
                value={branding.login_background_url || ''}
                onChange={(e) => handleChange('login_background_url', e.target.value)}
                placeholder="https://example.com/background.jpg"
              />
              <p className="text-xs text-muted-foreground">
                Full URL to background image. Recommended: High-resolution landscape image (1920x1080 or larger)
              </p>
            </div>

            {branding.login_background_url && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="border rounded-lg overflow-hidden">
                  <img 
                    src={branding.login_background_url} 
                    alt="Login Background Preview" 
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200"><text x="50%" y="50%" text-anchor="middle" fill="gray">Invalid Image URL</text></svg>';
                    }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="border-blue-500/50 bg-blue-500/5">
          <CardHeader>
            <CardTitle className="text-sm">📝 How to Upload Custom Logos</CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p><strong>Option 1: Direct Upload (Recommended)</strong></p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Upload your logo file to: <code className="bg-muted px-1 rounded">/app/frontend/public/</code></li>
              <li>Name it something like <code className="bg-muted px-1 rounded">my-logo.svg</code></li>
              <li>Enter <code className="bg-muted px-1 rounded">/my-logo.svg</code> in the Logo URL field above</li>
              <li>Click "Save Changes" and "Reload Page"</li>
            </ol>
            
            <p className="mt-4"><strong>Option 2: Use External URL</strong></p>
            <p>Enter a full URL like <code className="bg-muted px-1 rounded">https://example.com/logo.svg</code></p>
            
            <p className="mt-4 text-xs text-muted-foreground">
              <strong>Note:</strong> After saving, click "Reload Page" to see the new logo. SVG format recommended for best quality.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
